/**
 * AVS-4485: answers "is the tab with this slot id a connected VOIP peer?" for the session computeds.
 * `computed.ts` cannot import the VOIP connection (`utils/ui/rtc` imports the session), so
 * `state/session.ts` registers the real probe at load. The default answers "yes", the safe side: a
 * hand-off bystander then stays a standby and never claims on its own.
 */
let probe: (slotId: string) => boolean = () => true;

export function setVoipPeerProbe(next: (slotId: string) => boolean): void {
  probe = next;
}

export function isLiveVoipPeer(slotId: string): boolean {
  return probe(slotId);
}

const peerListeners = new Set<() => void>();

/**
 * AVS-4485: subscribe to changes of this tab's VOIP peer list. A hand-off bystander's standby status
 * depends on whether the named successor is still a peer, and the peer list lives only in the VOIP
 * store, so no session key changes when the successor leaves.
 */
export function onVoipPeersChanged(listener: () => void): () => void {
  peerListeners.add(listener);
  return () => peerListeners.delete(listener);
}

/** AVS-4485: called by the VOIP connection when its `remoteConnections` change. */
export function notifyVoipPeersChanged(): void {
  peerListeners.forEach((listener) => listener());
}

// Slot ids (per-session digests) whose VOIP row this tab saw REMOVED. Absence from the list or a
// failed transport is no proof a tab left (a rebuild empties the list; a failed transport keeps the row).
const departedSlots = new Set<string>();

/** AVS-4485: this tab saw the VOIP row of `slotId` removed (the tab left the call). */
export function markVoipPeerDeparted(slotId: string): void {
  departedSlots.add(slotId);
}

/** AVS-4485: the tab with `slotId` is back in the call. */
export function markVoipPeerPresent(slotId: string): void {
  departedSlots.delete(slotId);
}

export function hasVoipPeerDeparted(slotId: string): boolean {
  return departedSlots.has(slotId);
}

export function clearDepartedVoipPeers(): void {
  departedSlots.clear();
}
